import {
  PROPOSAL_CANCEL,
  PROPOSAL_SUBMITTED,
  SUBMIT_PROPOSAL_CLICKED,
} from "@js/apps/jobs/action-types";
import { getKeysOfAppliedURLParams } from "@js/middlewares/analytics/utils";
import { Events, EventsProperties } from "@js/services/analytics";
import { logEvent } from "@js/services/analytics/event-logging";
import type { FreelancerBid, Job } from "@js/types/jobs";
import { dateDifference, round } from "@js/utils";

import type { EventAction } from "../event-action";

const proposalViewedDateMap = new Map();
const appliedJobFiltersWhileCreatingProposal = new Map();

type SubmitProposalClickedAction = EventAction<
  typeof SUBMIT_PROPOSAL_CLICKED,
  { job: Job; search: string }
>;

type ProposalSubmittedAction = EventAction<
  typeof PROPOSAL_SUBMITTED,
  FreelancerBid
>;

type ProposalCanceledAction = EventAction<
  typeof PROPOSAL_CANCEL,
  FreelancerBid
>;

export type BidsAnalyticsActions =
  | SubmitProposalClickedAction
  | ProposalSubmittedAction
  | ProposalCanceledAction;

export const handleBidsAnalyticsActions = (action: BidsAnalyticsActions) => {
  switch (action.type) {
    case SUBMIT_PROPOSAL_CLICKED: {
      const { job, search } = action.payload;
      const appliedFilters = getKeysOfAppliedURLParams(search);
      proposalViewedDateMap.set(job.id, new Date().toISOString());
      appliedJobFiltersWhileCreatingProposal.set(job.id, appliedFilters);
      logEvent(Events.START_PROPOSAL, {
        job_id: job.id,
        [EventsProperties.filter_applied_type]: appliedFilters,
      });
      break;
    }

    case PROPOSAL_SUBMITTED: {
      const proposal = action.payload;
      const proposalViewedAt = proposalViewedDateMap.get(proposal.job.id);
      const appliedJobFilters = appliedJobFiltersWhileCreatingProposal.get(
        proposal.job.id,
      );
      const currentDate = new Date().toISOString();
      const timeBetweenViewAndProposalSubmission = round(
        dateDifference(currentDate, proposalViewedAt, "minutes"),
        0.25,
      );

      logEvent(Events.SUBMIT_PROPOSAL, {
        bid_id: proposal.id,
        proposal_submitted_time: timeBetweenViewAndProposalSubmission,
        [EventsProperties.filter_applied_type]: appliedJobFilters,
      });
      break;
    }

    case PROPOSAL_CANCEL: {
      const { id } = action.payload;

      logEvent(Events.CANCEL_PROPOSAL, { bid_id: id });
      break;
    }

    default: {
      break;
    }
  }
};
